import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { WorkpackService } from 'client/services';
import { logWithPrefix, LOG_LEVELS } from 'client/utils/logging';

const log = logWithPrefix('useWorkpackDownload');

// Shared workpack download logic for ListWorkpackPage and ViewWorkpackPage, keeping error handling/messaging consistent between them.
// messagePrefix selects the page-specific intl namespace (e.g. 'listWorkpackPage', 'viewWorkpackPage')
export const useWorkpackDownload = (messagePrefix) => {
    const intl = useIntl();
    const [downloadErrorMessage, setDownloadErrorMessage] = useState('');

    // Maps known service error statuses to page-specific messages, falling back to a generic error otherwise
    const getDownloadErrorMessage = useCallback((error) => {
        if (error?.statusCode === 404) {
            return intl.formatMessage({ id: `${messagePrefix}.download.error.notFound` });
        }

        if (error?.statusCode === 410) {
            return intl.formatMessage({ id: `${messagePrefix}.download.error.expired` });
        }

        return intl.formatMessage({ id: 'general.error' });
    }, [intl, messagePrefix]);

    const downloadWorkpack = useCallback(async (workpackRequestId, pack, title) => {
        // No downloadable file available for this workpack (e.g. still generating)
        if (!pack?.workpackId) {
            return;
        }

        try {
            setDownloadErrorMessage('');
            await WorkpackService.downloadWorkpack({
                workpackRequestId,
                workpackId: pack.workpackId,
                title,
                isNewPdf: pack.isNewPdf
            });
        } catch (error) {
            log('Failed to download workpack', JSON.stringify({ workpackReqId: workpackRequestId, error }), LOG_LEVELS.ERROR);
            setDownloadErrorMessage(getDownloadErrorMessage(error));
        }
    }, [getDownloadErrorMessage]);

    return { downloadErrorMessage, downloadWorkpack };
};

export default useWorkpackDownload;
